import React, { FC, useCallback } from 'react';
import { Redirect } from 'react-router-dom';

import { EvFlexColumn, EvSpinner } from '@evinced-private/ui-common';

import { LABEL_MANAGEMENT_PAGE_DAID } from 'data-automation-ids/LabelManagementAutomationIds';
import { useLabelManagementStore } from 'src/apps/dashboard/stores/labelManagementStore';
import { isConfigurationReady } from 'src/common/helpers/ConfigurationHelpers';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useUserTenant } from 'src/common/providers/userTenantProvider/UserTenantProvider';
import { DevelopmentToggles } from 'src/common/services/TogglesService';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { PAGE_HEADER_LABELS } from './components/page-header/LabelManagementPageHeader.labels';
import { LabelManagementPageHeader, LabelsTable } from './components';
import { PAGE_LABELS } from './LabelManagementPage.labels';
import { Label } from './types';

export const LabelManagementPage: FC = () => {
	const { hasAdminPermissions } = useUserTenant();
	const { configuration, getToggle } = useConfiguration();
	const { isInitialized } = usePlatformConfigStore();
	const { labels, openAddPanel, openEditPanel } = useLabelManagementStore();

	const canAccessLabelManagement = getToggle(DevelopmentToggles.CAN_ACCESS_LABEL_MANAGEMENT);

	const handleEdit = useCallback(
		(label: Label) => {
			openEditPanel(label);
		},
		[openEditPanel]
	);

	if (!hasAdminPermissions()) {
		return <Redirect to={RoutesHelper.getUnauthorized403Page()} />;
	}

	if (!isConfigurationReady(configuration)) {
		return <EvSpinner srMessage={PAGE_LABELS.LOADING} />;
	}

	if (!canAccessLabelManagement) {
		return <Redirect to={RoutesHelper.getUnauthorized403Page()} />;
	}

	if (!isInitialized) {
		return <EvSpinner srMessage={PAGE_LABELS.LOADING} />;
	}

	return (
		<EvFlexColumn dataAutomationId={LABEL_MANAGEMENT_PAGE_DAID} padding="LARGE" gap="XLARGE">
			<LabelManagementPageHeader
				title={PAGE_HEADER_LABELS.TITLE}
				subtitle={PAGE_HEADER_LABELS.SUBTITLE}
			/>
			<LabelsTable labels={labels} onEdit={handleEdit} onCreateLabel={openAddPanel} />
		</EvFlexColumn>
	);
};
